import {
  CourseId,
  CourseResponse,
  CourseStepResponse,
  CompactCourseResponse,
  UserCourseCountResponse,
  CourseSearchParams,
  CopyCourseTemplateRequest,
  CourseBlockTemplateResponse,
  VeryCompactCourseResponse,
} from '@business/entities/course';

import { Endpoint } from '../endpoint';
import { UserId } from '@business/entities/account';
import { ProposalTemplateResponse } from '@business/entities/proposal';
import { R } from '@core/utils/r';

export function fetchCourses(params?: CourseSearchParams) {
  return Endpoint.get<CompactCourseResponse[]>('@gc/courses', { params: R.objectCompact(params) });
}

export function fetchCourse(id: CourseId) {
  return Endpoint.get<CourseResponse>(`@gc/courses/course/${id}`);
}

export function createCourse(payload: Partial<CourseResponse>, userId?: UserId) {
  const data = R.objectCompact({ ...payload, userId: userId });
  return Endpoint.post<CourseResponse>(`@gc/courses/create`, { data });
}

export function duplicateCourse(id: CourseId, data: Partial<CourseResponse>) {
  return Endpoint.post<CourseResponse>(`@gc/courses/duplicate-course/${id}`, { data });
}

export function updateCourse(id: CourseId, data: Partial<CourseResponse>) {
  return Endpoint.patch<CourseResponse>(`@gc/courses/${id}`, { data });
}

export function updateCourseArchive(id: CourseId, data: Partial<CourseResponse>) {
  return Endpoint.patch<CourseResponse>(`@gc/courses/course-archive/${id}`, { data });
}

export function updateCourseStatus(id: CourseId, data: Partial<CourseResponse>) {
  return Endpoint.patch<CourseResponse>(`@gc/courses/course-status/${id}`, { data });
}

export function deleteCourse(id: CourseId) {
  return Endpoint.delete<void>(`@gc/courses/${id}`);
}

/** Section **/
export function createCourseSection(id: CourseId, name: string) {
  return Endpoint.post<void>(`@gc/courses/section/${id}`, { data: { name } });
}

export function updateCourseSection(id: number, name: string) {
  return Endpoint.patch<void>(`@gc/courses/section/${id}`, { data: { name } });
}

export function deleteCourseSection(id: number) {
  return Endpoint.delete<void>(`@gc/courses/section/${id}`);
}

export function sortSections(courseId: CourseId, ids: number[]) {
  return Endpoint.post<void>(`@gc/courses/sort-section`, { data: { ids, courseId } });
}

/** Step **/
export function createCourseStep(id: number, data: Partial<CourseStepResponse>) {
  return Endpoint.post<CourseStepResponse>(`@gc/courses/step/${id}`, { data });
}

export function updateCourseStep(id: number, data: Partial<CourseStepResponse>) {
  return Endpoint.patch<void>(`@gc/courses/step/${id}`, { data });
}

export function updateCourseStepName(id: number, name: string) {
  return Endpoint.patch<void>(`@gc/courses/step-name/${id}`, { data: { name } });
}

export function updateStepSection(id: number, sectionId: number) {
  return Endpoint.patch<void>(`@gc/courses/step-section/${id}`, { data: { sectionId } });
}

export function deleteCourseStep(id: number) {
  return Endpoint.delete<void>(`@gc/courses/step/${id}`);
}

export function stepCopy(courseId: CourseId, id: number) {
  return Endpoint.post<CourseResponse>(`@gc/courses/step-copy/${id}`);
}

export function sortSectionSteps(courseId: CourseId, ids: number[]) {
  return Endpoint.post<void>(`@gc/courses/sort-step`, { data: { ids, courseId } });
}

export function updateAssignne(type: string, id: number, items: string[]) {
  return Endpoint.post<CourseResponse>(`@gc/courses/assignee`, { data: { type, id, items } });
}

/* User Courses */
export function enroll(id: CourseId, uids: UserId[], isEmail?: boolean) {
  return Endpoint.post<CourseResponse>(`@gc/courses/enroll/${id}`, { data: { uids, isEmail } });
}

export function unenroll(id: CourseId, uid: UserId) {
  return Endpoint.post<CourseResponse>(`@gc/courses/unenroll/${id}`, { data: { uid } });
}

export function restart(id: CourseId, uid: UserId) {
  return Endpoint.post<CourseResponse>(`@gc/courses/restart/${id}`, { data: { uid } });
}

export function bulkRestart(id: CourseId, uids: UserId[]) {
  return Endpoint.post<CourseResponse>(`@gc/courses/bulk-restart/${id}`, { data: { uids } });
}

export function sortUserCourses(userId: UserId, courseIds: CourseId[]) {
  return Endpoint.post<void>(`@gc/courses/sort-user-courses`, { data: { userId, courseIds } });
}

export function fetchUserCourses(id: CourseId) {
  return Endpoint.get<UserCourseCountResponse>(`@gc/courses/course-count/${id}`);
}

export function updateFormData(id: string, data: string) {
  return Endpoint.post<void>(`@gc/courses/update-form-data/${id}`, { data: { data } });
}

export function getStep(id: string) {
  return Endpoint.get<CourseStepResponse>(`@gc/courses/step/${id}`);
}

export function copyBlockTemplate(data: CopyCourseTemplateRequest) {
  return Endpoint.post<void>(`@gc/courses/copy-template`, { data });
}

export function fetchBlockTemplates() {
  return Endpoint.get<CourseBlockTemplateResponse[]>(`@gc/courses/block-templates`);
}

export function searchCourses(params?: CourseSearchParams) {
  return Endpoint.get<VeryCompactCourseResponse[]>('@gc/courses/search-courses', { params: R.objectCompact(params) });
}
